import React from 'react';
import { colors } from '../../../web-polyfills/native-app-theme/colors';
import { ui } from '../../../web-polyfills/native-app-theme/ui';
import { NewsItemFormValues } from '../../../models/NewsItem';

/**
 * Live preview of how a News card renders on the app Home screen (the News
 * section sits just after "Upcoming opportunities"). Mirrors
 * fides-capital-native-app/src/screens/home/NewsCardContent.tsx +
 * homeNewsStyles.ts / homeHighlightStyles.ts — same phone-frame approach as
 * MobileAppPreview. Keep visuals in sync if the native card changes.
 */

const PHONE_WIDTH = 320;
const FRAME_PADDING = 10;
const CARD_INNER = PHONE_WIDTH - 32; // Card.Content horizontal padding on the app

function formatDateForChart(dateStr: string): string {
  // Matches native utils/currency.ts formatDateForChart: "27 JUN 26"
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr || '');
  const MONTHS = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];
  if (!m) return (dateStr || '').toUpperCase();
  const [, y, mo, d] = m;
  return `${Number(d)} ${MONTHS[Number(mo) - 1]} ${y.slice(2)}`;
}

export default function NewsCardPreview({ values }: { values: NewsItemFormValues }) {
  const minutes = parseInt(values.minutesToRead, 10);
  const meta = [formatDateForChart(values.publishDate)];
  if (Number.isFinite(minutes) && minutes > 0) meta.push(`${minutes} min read`);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        background: '#e8eaed',
        padding: '20px 0',
        borderRadius: 16,
        minHeight: '100%',
      }}
    >
      <div
        style={{
          width: PHONE_WIDTH + FRAME_PADDING * 2,
          background: '#1c1c1e',
          borderRadius: 40,
          padding: FRAME_PADDING,
          boxShadow: '0 20px 48px rgba(0,0,0,0.3)',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: PHONE_WIDTH,
            background: colors.background,
            borderRadius: 30,
            overflow: 'hidden',
          }}
        >
          {/* app top bar */}
          <div
            style={{
              height: 44,
              background: colors.surface,
              borderBottom: `1px solid ${colors.borderLight}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              fontWeight: 600,
              color: colors.text,
            }}
          >
            Home
          </div>

          <div style={{ padding: 16 }}>
            <div
              style={{
                fontSize: 11,
                color: colors.textSecondary,
                textAlign: 'center',
                marginBottom: 8,
              }}
            >
              ⋯ Upcoming opportunities ⋯
            </div>

            {/* News section Card */}
            <div
              style={{
                background: colors.surface,
                borderRadius: ui.cardRadius,
                padding: 16,
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: 10,
                }}
              >
                News
              </div>

              {/* NewsCardContent slide */}
              <div
                style={{
                  border: `1px solid ${colors.borderLight}`,
                  borderRadius: ui.panelRadius,
                  background: colors.surfaceVariant,
                  padding: 12,
                  width: CARD_INNER,
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: colors.textSecondary,
                    marginBottom: 4,
                  }}
                >
                  {meta.join('  ·  ')}
                </div>

                {values.title ? (
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: colors.text,
                      marginBottom: 6,
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {values.title}
                  </div>
                ) : null}

                {values.imageUrl ? (
                  <img
                    src={values.imageUrl}
                    alt=""
                    style={{
                      width: '100%',
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: 8,
                      marginBottom: 8,
                      display: 'block',
                    }}
                  />
                ) : null}

                {values.summary ? (
                  <div
                    style={{
                      fontSize: 13,
                      lineHeight: '20px',
                      color: colors.textSecondary,
                      marginBottom: 6,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {values.summary}
                  </div>
                ) : null}

                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    color: colors.primaryDarker,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Read article <span aria-hidden>→</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
